import { useCallback } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
// @ts-ignore
import pdfMake from "pdfmake/build/pdfmake";
import { fetchQuotationById, changeQuotationStatusById } from "slices/Quotation/thunk";
import { generateQuotationPDFWithoutRateTotal } from "helpers/PDF/generateQuotationPDFWithoutRateTotal";



export const useHandleDownloadPIInvoice2 = () => {
  const dispatch = useDispatch<any>();

  const handlePIInvoice2 = useCallback(
    async (id: any, onSuccess?: () => void) => {
      // Fetch quotation data once for both status check and PDF generation
      let quotationData;
      try {
        quotationData = await dispatch(fetchQuotationById(id)).unwrap();
      } catch (error) {
        console.error("Failed to fetch quotation data:", error);
        toast.error("Failed to load quotation data. Please try again.");
        return;
      }

      const currentStatus = quotationData.current_status;

      // Only show confirmation dialog if status is not "PI Generated"
      if (currentStatus !== "PI Generated") {
        try {
          const confirmResult = await Swal.fire({
            title: "Generate Pro Forma Invoice?",
            text: "After generating PI Invoice, the quotation status will be updated to PI Generated. Are you sure you want to proceed?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, Generate PI",
            cancelButtonText: "Cancel",
            customClass: { popup: "swal2-sm-popup" },
          });

          if (!confirmResult.isConfirmed) {
            return;
          }
        } catch (error) {
          console.error("Confirmation dialog error:", error);
          return;
        }
      }

      //*// Frontend PI Invoice 2 Download (without Rate and Total columns)
      let isCancelled = false;

      Swal.fire({
        title: "Generating...",
        html: `
        <p>Please wait while your Pro Forma Invoice is being generated.</p>
        <button id="cancelPI" class="swal2-cancel swal2-styled" style="display:inline-block">
          Cancel
        </button>
      `,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          const cancelBtn = Swal.getPopup()?.querySelector("#cancelPI");
          if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
              isCancelled = true;
              Swal.close();
              Swal.fire({
                icon: "info",
                title: "Cancelled",
                timer: 1000,
                showConfirmButton: false,
                customClass: { popup: "swal2-sm-popup" },
              });
            });
          }
        },
        customClass: { popup: "swal2-sm-popup" },
      });

      try {
        // Check if cancelled after data fetch
        if (isCancelled) return;

        const quotationPDF = await generateQuotationPDFWithoutRateTotal(
          quotationData,
          'PRO FORMA INVOICE'
        );

        // Check if cancelled after PDF generation
        if (isCancelled) return;

        // Set filename in the PDF document definition
        const filename = quotationData?.quotation_auto_id ? `PI-${quotationData.quotation_auto_id}.pdf` : 'PI-Invoice.pdf';
        const pdfDocWithInfo = quotationPDF as any;
        if (pdfDocWithInfo.info) {
          pdfDocWithInfo.info.title = filename.replace('.pdf', '');
        } else {
          pdfDocWithInfo.info = { title: filename.replace('.pdf', '') };
        }

        // Download PDF directly with correct filename
        if (!isCancelled) {
          pdfMake.createPdf(quotationPDF).download(filename);

          // Update quotation status to "PI Generated" after successful PDF generation
          // Only update if current status is not already "PI Generated"
          if (quotationData.current_status !== "PI Generated") {
            try {
              await dispatch(changeQuotationStatusById({ id: id, payload: "PI Generated" })).unwrap();
              console.log("Quotation status updated to PI Generated");
              // Call success callback to refresh data
              if (onSuccess) {
                onSuccess();
              }
            } catch (statusError: any) {
              console.error("Failed to update quotation status:", statusError);
              // Don't show error to user as PDF was successfully generated
            }
          }

          Swal.close();
        }
        // pdfDoc.getBlob((blob: Blob) => {
        //   // Create a blob with explicit MIME type
        //   const pdfBlob = new Blob([blob], { type: "application/pdf" });
        //   const url = URL.createObjectURL(pdfBlob);

        //   // Use a more direct approach
        //   const link = document.createElement("a");
        //   link.href = url;
        //   link.download = `quotation-${quotationData.quotation_auto_id}.pdf`;
        //   link.style.display = "none";
        //   link.setAttribute(
        //     "download",
        //     `quotation-${quotationData.quotation_auto_id}.pdf`
        //   );

        //   // Add to DOM, click, and remove immediately
        //   document.body.appendChild(link);
        //   setTimeout(() => {
        //     link.click();
        //     document.body.removeChild(link);
        //     URL.revokeObjectURL(url);
        //   }, 0);
        // });

        Swal.close();
      } catch (err: any) {
        if (!isCancelled) {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: "Failed to generate Pro Forma Invoice PDF.",
            customClass: { popup: "swal2-sm-popup" },
          });
        }
      }
    },
    [dispatch]
  );

  return handlePIInvoice2;
};

export const useHandleDownloadPDF2 = () => {
  const dispatch = useDispatch<any>();

  const handleDownloadPDF2 = useCallback(
    async (id: any) => {
      //*// Frontend PDF Download (without Rate and Total columns)
      let isCancelled = false;

      Swal.fire({
        title: "Generating PDF...",
        html: `
        <p>Please wait while your PDF is being generated.</p>
        <button id="cancelPDF" class="swal2-cancel swal2-styled" style="display:inline-block">
          Cancel
        </button>
      `,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
          const cancelBtn = Swal.getPopup()?.querySelector("#cancelPDF");
          if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
              isCancelled = true;
              Swal.close();
              Swal.fire({
                icon: "info",
                title: "Cancelled",
                timer: 1000,
                showConfirmButton: false,
                customClass: { popup: "swal2-sm-popup" },
              });
            });
          }
        },
        customClass: { popup: "swal2-sm-popup" },
      });

      try {
        // Fetch quotation data
        const quotationData = await dispatch(fetchQuotationById(id)).unwrap();

        // Check if cancelled after data fetch
        if (isCancelled) return;

        const quotationPDF = await generateQuotationPDFWithoutRateTotal(
          quotationData,
          'QUOTATION'
        );

        // Check if cancelled after PDF generation
        if (isCancelled) return;

        // Set filename in the PDF document definition
        const filename = quotationData?.quotation_auto_id ? `Quotation-${quotationData.quotation_auto_id}.pdf` : 'Quotation.pdf';
        const pdfDocWithInfo = quotationPDF as any;
        if (pdfDocWithInfo.info) {
          pdfDocWithInfo.info.title = filename.replace('.pdf', '');
        } else {
          pdfDocWithInfo.info = { title: filename.replace('.pdf', '') };
        }

        // Download PDF directly with correct filename
        if (!isCancelled) {
          pdfMake.createPdf(quotationPDF).download(filename);
          Swal.close();
        }
        // Swal.fire({
        //   icon: "success",
        //   title: "PDF Downloaded Successfully",
        //   timer: 2000,
        //   showConfirmButton: false,
        //   customClass: { popup: "swal2-sm-popup" },
        // });
      } catch (err: any) {
        if (!isCancelled) {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: "Failed to generate quotation PDF.",
            customClass: { popup: "swal2-sm-popup" },
          });
        }
      }
    },
    [dispatch]
  );

  return handleDownloadPDF2;
};